import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { baixarCSV } from '../utils';

export type SituacaoCamiseta = 'com_vaga' | 'suplentes' | 'todos';
export type PapelCamiseta = 'equipe' | 'campista';

interface Props {
  eventoId: number;
  eventoNome: string;
  /** De quem é o relatório. Em evento sem equipe/campista, null. */
  papel?: PapelCamiseta | null;
  situacaoInicial?: SituacaoCamiseta;
  /** O relatório já vem de uma lista filtrada: a situação não pode ser trocada. */
  travarSituacao?: boolean;
  /** Nome da situação quando o relatório vem travado pela lista (ex.: "suplentes"). */
  rotuloSituacao?: string;
  /** Ids da lista filtrada que está na tela. */
  inscricaoIds?: number[] | null;
}

interface LinhaCamiseta {
  tamanho: string | null;
  quem: string | null;
  quantidade: number;
}

const OPCOES: { valor: SituacaoCamiseta; rotulo: string }[] = [
  { valor: 'com_vaga', rotulo: 'Com vaga' },
  { valor: 'suplentes', rotulo: 'Suplentes' },
  { valor: 'todos', rotulo: 'Todos' },
];

const STATUS_POR_SITUACAO: Record<SituacaoCamiseta, string[]> = {
  com_vaga: ['pendente', 'confirmada', 'isenta'],
  suplentes: ['lista_espera'],
  todos: ['pendente', 'confirmada', 'isenta', 'lista_espera'],
};

const ORDEM_TAMANHO = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG', 'G1', 'G2', 'G3', 'G4'];

function ehConferir(quem: string | null): boolean {
  return /sem resposta/i.test(quem ?? '');
}

function ehSemTamanho(tamanho: string): boolean {
  return tamanho.trim().toLowerCase() === '(sem tamanho)';
}

function posicaoTamanho(tamanho: string): number {
  if (ehSemTamanho(tamanho)) return 999;
  const i = ORDEM_TAMANHO.indexOf(tamanho.trim().toUpperCase());
  return i === -1 ? 98 : i;
}

function slug(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export default function RelatorioCamisetas({
  eventoId,
  eventoNome,
  papel = null,
  situacaoInicial = 'com_vaga',
  travarSituacao = false,
  rotuloSituacao,
  inscricaoIds = null,
}: Props) {
  const [situacao, setSituacao] = useState<SituacaoCamiseta>(situacaoInicial);
  const [linhas, setLinhas] = useState<LinhaCamiseta[]>([]);
  const [pessoas, setPessoas] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // A lista filtrada pode chegar depois (ex.: a lista da aba ainda está carregando).
  const chaveIds = useMemo(
    () => (inscricaoIds ? inscricaoIds.join(',') : 'todos'),
    [inscricaoIds],
  );

  useEffect(() => {
    setSituacao(situacaoInicial);
  }, [situacaoInicial]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      const ids = inscricaoIds && inscricaoIds.length > 0 ? inscricaoIds : null;
      // Lista filtrada vazia: nada a contar (não é "o evento inteiro").
      const listaVazia = Array.isArray(inscricaoIds) && inscricaoIds.length === 0;
      try {
        if (listaVazia) {
          if (!ativo) return;
          setLinhas([]);
          setPessoas(0);
          return;
        }

        const consultaInscricoes = (() => {
          let q = supabase
            .from('vw_inscritos')
            .select('inscricao_id, modalidade_id, status')
            .eq('evento_id', eventoId)
            .in('status', STATUS_POR_SITUACAO[situacao]);
          if (ids) q = q.in('inscricao_id', ids);
          return q;
        })();

        const [resRpc, resMods, resInsc] = await Promise.all([
          supabase.rpc('relatorio_camisetas', {
            p_evento: eventoId,
            p_situacao: situacao,
            p_papel: papel,
            p_inscricoes: ids,
          }),
          papel
            ? supabase
                .from('modalidades')
                .select('id')
                .eq('evento_id', eventoId)
                .eq('papel', papel)
            : Promise.resolve({ data: null, error: null }),
          consultaInscricoes,
        ]);

        if (resRpc.error) throw resRpc.error;
        if (resMods.error) throw resMods.error;
        if (resInsc.error) throw resInsc.error;
        if (!ativo) return;

        const idsDoPapel = new Set(
          ((resMods.data as { id: number }[] | null) || []).map((m) => Number(m.id)),
        );
        const quantasPessoas = (
          (resInsc.data as { modalidade_id: number }[] | null) || []
        ).filter((i) => !papel || idsDoPapel.has(Number(i.modalidade_id))).length;

        setLinhas((resRpc.data as LinhaCamiseta[]) || []);
        setPessoas(quantasPessoas);
      } catch (e) {
        if (ativo) setErro(mensagemAmigavel(e, 'Não foi possível gerar o relatório.'));
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId, papel, situacao, chaveIds]);

  // Colunas vêm do "quem" que o banco devolve — nunca fixas em Esposo/Esposa/Pessoa.
  const colunas = useMemo(() => {
    const vistas: string[] = [];
    linhas.forEach((l) => {
      const coluna = (l.quem ?? '').trim() || 'Pessoa';
      if (!vistas.includes(coluna)) vistas.push(coluna);
    });
    return [...vistas.filter((c) => !ehConferir(c)), ...vistas.filter((c) => ehConferir(c))];
  }, [linhas]);

  const tabela = useMemo(() => {
    const mapa = new Map<string, Map<string, number>>();
    linhas.forEach((l) => {
      const tamanho = (l.tamanho ?? '').trim() || '(sem tamanho)';
      const coluna = (l.quem ?? '').trim() || 'Pessoa';
      if (!mapa.has(tamanho)) mapa.set(tamanho, new Map());
      const atual = mapa.get(tamanho)!;
      atual.set(coluna, (atual.get(coluna) ?? 0) + Number(l.quantidade || 0));
    });
    return [...mapa.entries()]
      .map(([tamanho, valores]) => ({
        tamanho,
        valores,
        total: colunas.reduce((acc, c) => acc + (valores.get(c) ?? 0), 0),
      }))
      .sort((a, b) => {
        const pa = posicaoTamanho(a.tamanho);
        const pb = posicaoTamanho(b.tamanho);
        if (pa !== pb) return pa - pb;
        return a.tamanho.localeCompare(b.tamanho, 'pt-BR', { numeric: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhas, colunas]);

  const totaisColuna = useMemo(() => {
    const mapa = new Map<string, number>();
    colunas.forEach((c) => {
      mapa.set(
        c,
        tabela.reduce((acc, t) => acc + (t.valores.get(c) ?? 0), 0),
      );
    });
    return mapa;
  }, [tabela, colunas]);

  const totalGeral = useMemo(
    () => tabela.reduce((acc, t) => acc + t.total, 0),
    [tabela],
  );

  const temConferir = useMemo(
    () =>
      colunas.some((c) => ehConferir(c)) || tabela.some((t) => ehSemTamanho(t.tamanho)),
    [colunas, tabela],
  );

  // Peças de verdade: fora a coluna "Sem resposta" (não é peça) e as linhas sem tamanho.
  const conferirTotal = useMemo(
    () =>
      colunas
        .filter((c) => ehConferir(c))
        .reduce((acc, c) => acc + (totaisColuna.get(c) ?? 0), 0),
    [colunas, totaisColuna],
  );

  const semTamanhoTotal = useMemo(
    () =>
      tabela
        .filter((t) => ehSemTamanho(t.tamanho))
        .reduce(
          (acc, t) =>
            acc +
            colunas
              .filter((c) => !ehConferir(c))
              .reduce((soma, c) => soma + (t.valores.get(c) ?? 0), 0),
          0,
        ),
    [tabela, colunas],
  );

  const totalPecas = Math.max(0, totalGeral - conferirTotal - semTamanhoTotal);
  const paraConferir = conferirTotal + semTamanhoTotal;

  const rotuloSituacaoTxt =
    travarSituacao && rotuloSituacao
      ? rotuloSituacao
      : (OPCOES.find((o) => o.valor === situacao)?.rotulo ?? '');

  const titulo = [
    eventoNome,
    'Camisetas',
    papel === 'equipe' ? 'EQUIPE' : papel === 'campista' ? 'CAMPISTAS' : null,
    rotuloSituacaoTxt.toLowerCase(),
    `${pessoas} ${pessoas === 1 ? 'pessoa' : 'pessoas'}`,
    `${totalPecas} ${totalPecas === 1 ? 'peça' : 'peças'}`,
    paraConferir > 0 ? `${paraConferir} sem resposta` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  function baixar() {
    const cabecalho = ['Tamanho', ...colunas, 'Total'];
    const corpo = tabela.map((t) => [
      t.tamanho,
      ...colunas.map((c) => String(t.valores.get(c) ?? 0)),
      String(t.total),
    ]);
    const rodape = [
      'Total',
      ...colunas.map((c) => String(totaisColuna.get(c) ?? 0)),
      String(totalGeral),
    ];
    baixarCSV(`camisetas-${papel ? `${papel}-` : ''}${slug(rotuloSituacaoTxt) || situacao}-${slug(eventoNome)}.csv`, [
      [titulo],
      [],
      cabecalho,
      ...corpo,
      rodape,
    ]);
  }

  return (
    <section>
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body * { visibility: hidden; }
          #relatorio-camisetas, #relatorio-camisetas * { visibility: visible; }
          #relatorio-camisetas { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {travarSituacao ? (
          <p className="text-[12px] font-semibold uppercase tracking-wide text-foreground-500">
            {rotuloSituacaoTxt}
          </p>
        ) : (
          <div className="flex gap-1 rounded-full bg-background-100 p-1">
            {OPCOES.map((o) => (
              <button
                key={o.valor}
                type="button"
                onClick={() => setSituacao(o.valor)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                  situacao === o.valor
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {o.rotulo}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={baixar}
            disabled={tabela.length === 0}
            className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-3 text-[12px] font-semibold text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-download-2-line text-sm" />
            Baixar
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            disabled={tabela.length === 0}
            className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-3 text-[12px] font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-printer-line text-sm" />
            Imprimir
          </button>
        </div>
      </div>

      {carregando ? (
        <div className="mt-3 h-40 animate-pulse rounded-lg bg-secondary-100" />
      ) : erro ? (
        <p className="mt-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-[13px] text-danger-700">
          {erro}
        </p>
      ) : tabela.length === 0 ? (
        <p className="mt-3 rounded-lg border border-secondary-200 bg-white px-4 py-6 text-center text-[13px] text-foreground-600">
          Nenhuma camiseta registrada nessa situação.
        </p>
      ) : (
        <div id="relatorio-camisetas" className="mt-3 rounded-lg border border-secondary-200 bg-white p-4">
          <header className="border-b border-secondary-200 pb-2">
            <p className="text-[13px] font-bold leading-snug text-foreground-950">{titulo}</p>
            <p className="text-[12px] text-foreground-500">
              {pessoas} {pessoas === 1 ? 'pessoa' : 'pessoas'} nesta conta ·{' '}
              {totalPecas} {totalPecas === 1 ? 'peça' : 'peças'}
              {paraConferir > 0 ? ` · ${paraConferir} sem resposta` : ''}
            </p>
            {temConferir && (
              <p className="mt-1 text-[12px] font-semibold text-danger-600">
                conferir antes de pedir
              </p>
            )}
          </header>

          <div className="overflow-x-auto">
            <table className="mt-2 w-full min-w-[420px] border-collapse text-left">
              <thead>
                <tr className="border-b border-secondary-300 text-[11px] uppercase tracking-wide text-secondary-500">
                  <th className="py-1.5 pr-3 font-semibold">Tamanho</th>
                  {colunas.map((c) => (
                    <th
                      key={c}
                      className={`py-1.5 pr-3 text-right font-semibold ${
                        ehConferir(c) ? 'text-danger-600' : ''
                      }`}
                    >
                      {c}
                    </th>
                  ))}
                  <th className="py-1.5 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {tabela.map((t) => {
                  const semTamanho = ehSemTamanho(t.tamanho);
                  return (
                    <tr
                      key={t.tamanho}
                      className={`border-b border-secondary-100 text-[13px] ${
                        semTamanho ? 'text-danger-700' : ''
                      }`}
                    >
                      <td
                        className={`py-1.5 pr-3 font-semibold ${
                          semTamanho ? 'text-danger-700' : 'text-foreground-950'
                        }`}
                      >
                        {t.tamanho}
                        {semTamanho && (
                          <span className="ml-1 text-[11px] font-medium">conferir antes de pedir</span>
                        )}
                      </td>
                      {colunas.map((c) => (
                        <td
                          key={c}
                          className={`py-1.5 pr-3 text-right ${
                            ehConferir(c) ? 'font-semibold text-danger-700' : 'text-foreground-800'
                          }`}
                        >
                          {t.valores.get(c) ?? 0}
                        </td>
                      ))}
                      <td className="py-1.5 text-right font-bold text-foreground-950">{t.total}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="text-[13px] font-bold text-foreground-950">
                  <td className="py-1.5 pr-3">Total</td>
                  {colunas.map((c) => (
                    <td
                      key={c}
                      className={`py-1.5 pr-3 text-right ${
                        ehConferir(c) ? 'text-danger-700' : ''
                      }`}
                    >
                      {totaisColuna.get(c) ?? 0}
                    </td>
                  ))}
                  <td className="py-1.5 text-right text-primary-700">{totalGeral}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}